import { Injectable } from '@angular/core';
import { AnalyticsEntry } from '../models/AnalyticsEntry';

interface StorageServiceInterface {
  get<T extends StorageKey>(key: T): StorageItemType[T];

  set<T extends StorageKey>(key: T, value: StorageItemType[T]): void;

  has(key: StorageKey): boolean;
}

interface SerializersPair {
  serialize: (data: StorageItemType[StorageKey]) => string;
  deserialize: (data: string) => StorageItemType[StorageKey];
}

const Serializers: Record<string, SerializersPair> = {
  STRING: {
    serialize: (data: string) => data,
    deserialize: (data: string) => data,
  },
  DATE: {
    serialize: (data: Date) => data.toISOString(),
    deserialize: (data: string) => new Date(data),
  },
  BOOLEAN: {
    serialize: (data: boolean) => String(data),
    deserialize: (data: string) => data === 'true',
  },
  JSON: {
    serialize: (data: unknown) => JSON.stringify(data),
    deserialize: (data: string) => JSON.parse(data),
  },
  NUMBER: {
    serialize: (data: number) => String(data),
    deserialize: (data: string) => Number(data),
  },
};

export enum StorageKey {
  UUID = 'uuid',
  NOTIFICATION_TIMER = 'notificationTimer',
  INTRODUCTION = 'introduction',
  ANALYTICS_DATA = 'analyticsData',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  FIRST_LOGIN_DATE = 'firstLoginDate',
  SHOW_END_DATE_REQUEST = 'showEndDateRequest',
  SHOW_END_DATE_FIRST_REQUEST = 'showEndDateFirstRequest',
  WELCOME_MESSAGE_DATE = 'welcomeMessageDate',
  MISSIONS_FINISHED_TODAY = 'missionsFinishedToday',
  ACTIVATED = 'activated',
  FINISHED_MISSIONS = 'finishedMissions',
  CURRENTLY_SHOWING_MISSIONS = 'currentlyShowingMissionsIds',
  UPDATED_SLICE_AMOUNT = 'updatedSliceAmountDate',
  PREVIOUSLY_SHOWN_MISSIONS = 'previouslyShownMissions',
  SHOW_REQUEST_END_DATE_ON_SECOND_DAY = 'showRequestEndDateOnSecondDay',
  BACKGROUND_DATE = 'backgroundDate',
}

type StorageItemType = {
  [StorageKey.UUID]: string;
  [StorageKey.NOTIFICATION_TIMER]: Date;
  [StorageKey.INTRODUCTION]: boolean;
  [StorageKey.ANALYTICS_DATA]: AnalyticsEntry[];
  [StorageKey.START_DATE]: Date;
  [StorageKey.END_DATE]: Date;
  [StorageKey.FIRST_LOGIN_DATE]: Date;
  [StorageKey.SHOW_END_DATE_REQUEST]: Date;
  [StorageKey.SHOW_END_DATE_FIRST_REQUEST]: boolean;
  [StorageKey.WELCOME_MESSAGE_DATE]: Date;
  [StorageKey.MISSIONS_FINISHED_TODAY]: number;
  [StorageKey.ACTIVATED]: boolean;
  [StorageKey.FINISHED_MISSIONS]: number[];
  [StorageKey.CURRENTLY_SHOWING_MISSIONS]: number[];
  [StorageKey.UPDATED_SLICE_AMOUNT]: Date;
  [StorageKey.PREVIOUSLY_SHOWN_MISSIONS]: number[];
  [StorageKey.SHOW_REQUEST_END_DATE_ON_SECOND_DAY]: boolean;
  [StorageKey.BACKGROUND_DATE]: Date;
};

/**
 * For every key in StorageKey you need to add a serializer and deserializer.
 * Luckily, most of the time you can use one of the predefined serializers
 */
const storageItemSerializers: Record<StorageKey, SerializersPair> = {
  [StorageKey.UUID]: Serializers.STRING,
  [StorageKey.NOTIFICATION_TIMER]: Serializers.DATE,
  [StorageKey.INTRODUCTION]: Serializers.BOOLEAN,
  [StorageKey.ANALYTICS_DATA]: Serializers.JSON,
  [StorageKey.START_DATE]: Serializers.DATE,
  [StorageKey.END_DATE]: Serializers.DATE,
  [StorageKey.FIRST_LOGIN_DATE]: Serializers.DATE,
  [StorageKey.SHOW_END_DATE_REQUEST]: Serializers.DATE,
  [StorageKey.SHOW_END_DATE_FIRST_REQUEST]: Serializers.BOOLEAN,
  [StorageKey.WELCOME_MESSAGE_DATE]: Serializers.DATE,
  [StorageKey.MISSIONS_FINISHED_TODAY]: Serializers.NUMBER,
  [StorageKey.ACTIVATED]: Serializers.BOOLEAN,
  [StorageKey.FINISHED_MISSIONS]: Serializers.JSON,
  [StorageKey.CURRENTLY_SHOWING_MISSIONS]: Serializers.JSON,
  [StorageKey.UPDATED_SLICE_AMOUNT]: Serializers.DATE,
  [StorageKey.PREVIOUSLY_SHOWN_MISSIONS]: Serializers.JSON,
  [StorageKey.SHOW_REQUEST_END_DATE_ON_SECOND_DAY]: Serializers.BOOLEAN,
  [StorageKey.BACKGROUND_DATE]: Serializers.DATE,
};

@Injectable({
  providedIn: 'root',
})
export class StorageService implements StorageServiceInterface {
  constructor() {}

  /**
   * Retrieve a value from local storage. The value will be automatically deserialized
   * @param key
   */
  get<T extends StorageKey>(key: T): StorageItemType[T] {
    const stored = localStorage.getItem(key);
    if (stored === null) return null;
    return storageItemSerializers[key].deserialize(stored) as StorageItemType[T];
  }

  /**
   * Store a value in local storage. The value will be automatically serialized before being stored
   * @param key
   * @param value
   */
  set<T extends StorageKey>(key: T, value: StorageItemType[T]) {
    return localStorage.setItem(key, storageItemSerializers[key].serialize(value));
  }

  /**
   * Check if a value exists in local storage.
   * Using this method is slightly faster than `this.get(key) !== null` because it doesn't need to deserialize
   * @param key
   */
  has(key: StorageKey) {
    return localStorage.getItem(key) !== null;
  }
}
